import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import {
	FrontendUserInfo,
	FrontendOrder,
	FrontendTestReport,
	FrontendProductOrder,
} from "node-library/dist/portal";
import { Card, Divider, Drawer } from "@mui/material";
import { OrderTableSortColumn, SortOrder } from "node-library/dist/paginations";
import {
	NeutralColor,
	PrimaryColor,
	SemanticColor,
} from "node-library/dist/colors";
import {
	DownloadIcon,
	FolderIcon,
	SortArrowIcon,
} from "../../../../common-components/svgIcons";

import { Severity, TurnaroundTime } from "node-library/dist/constants";
import { getOrderAvailableTAT } from "../../../../helpers/orderHelper";
import { getShortTurnaroundTime } from "../../../../helpers/sampleHelper";
import { CustomRadioGroup } from "../../../../common-components/inputs";
import {
	SkyBlueButton,
	BlueButton,
	KebabDropdownButton,
} from "../../../../common-components/button";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../../../requestHelper/sendRequest";
import {
	RedErrorIcon,
	RedTrashIcon,
} from "../../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import { formatCustomDate } from "node-library/dist/helpers/date_helper";

import { CustomDrawer } from "../../../../common-components/drawers/drawer";
import ClientOrderReportDrawerSkeletonUI from "../skeletonUI/clientOrderReportDrawerSkeleton";
import { handleDownloadAll } from "../../../../helpers/frontendFileOperationHelper";

interface Props {
	selectedOrder: FrontendProductOrder | undefined;
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function ClientViewOrderReportDrawer(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { selectedOrder, open, setOpen } = props;

	const orderTAT: Array<TurnaroundTime> =
		// selectedOrder
		// 	? getOrderAvailableTAT(selectedOrder)
		// 	:
		[];

	const [loading, setLoading] = useState<boolean>(false);
	const [selectedFolder, setSeletedFolder] = useState<TurnaroundTime>(
		orderTAT[0]
	);
	const [testReports, setTestReports] = useState<Array<FrontendTestReport>>([]);

	const testReportsInFolder: Array<FrontendTestReport> = testReports
		.filter((report) => {
			return report.turnaroundTime == selectedFolder;
		})
		.sort((a, b) => {
			if (a.clientDownloaded === null && b.clientDownloaded !== null) {
				return -1; // a comes first
			} else if (a.clientDownloaded !== null && b.clientDownloaded === null) {
				return 1; // b comes first
			} else {
				return (
					new Date(b.inserted as Date).getTime() -
					new Date(a.inserted as Date).getTime()
				); // descending order
			}
		});

	async function getOrderTestReports() {
		setLoading(true);
		try {
			const response = await sendGETRequest("/test-reports/get-order-reports", {
				orderId: (selectedOrder as FrontendProductOrder).orderId,
			});

			const reports: Array<FrontendTestReport> = response?.data?.reports;
			setTestReports(reports);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	async function downloadTestReports(reportIds: Array<string>) {
		setLoading(true);
		try {
			const response = await sendPOSTRequest(
				"/test-reports/download-test-reports",
				{
					reportIds,
					orderId: (selectedOrder as FrontendProductOrder).orderId,
				}
			);
			const fileAndSignedURLs: Array<{
				fileName: string;
				format: string;
				signedURL: string;
			}> = response.data.fileAndsignedURLs;

			const downloadResults: Array<{
				fullName: string;
				success: boolean;
			}> = await handleDownloadAll(fileAndSignedURLs);

			const errorFiles: Array<{
				fullName: string;
				success: boolean;
			}> = downloadResults.filter((result) => {
				return result.success == false;
			});

			if (errorFiles.length === 0) {
				// openSnackbar(
				// 	translation("SomeReportDownloaded", {
				// 		count: downloadResults.length,
				// 	}),
				// 	Severity.SUCCESS,
				// 	undefined,
				// 	<GreenTickIcon />
				// );
			} else {
				openSnackbar(
					translation("SomeReportDownloadedFailed", {
						count: downloadResults.length,
					}),
					Severity.ERROR,
					translation("SomeReportDownloadedFailedMsg", {
						files: errorFiles
							.map((file) => {
								return file.fullName;
							})
							.join(", "),
					}),
					<RedErrorIcon />
				);
			}
			const currTime: Date = new Date();

			setTestReports((prevReports) =>
				prevReports.map((prevReport, idx) =>
					reportIds.includes(prevReport.reportId)
						? { ...prevReport, clientDownloaded: currTime }
						: prevReport
				)
			);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		if (selectedOrder && open) {
			getOrderTestReports();
			setSeletedFolder(orderTAT[0]);
		}
	}, [selectedOrder, open]);

	const iconCardRadioOptions: Array<{
		key: string;
		name: string;
		value: any;
		children?: React.ReactNode;
	}> = orderTAT.map((tat: TurnaroundTime) => {
		const tatString: string = getShortTurnaroundTime(tat, translation);
		return {
			key: tat,
			name: tatString,
			value: tat,
			children: (
				<div
					className={`client-view-test-report-radio-option ${
						selectedFolder === tat
							? "client-view-test-report-radio-option-checked"
							: ""
					} `}
					tabIndex={0}
				>
					<div className={`test-report-folder-icon-container`}>
						<FolderIcon></FolderIcon>
					</div>

					<div
						className="typography-body-02 overflow-ellipsis-text"
						style={{ margin: "5px 2px" }}
					>
						{tatString}
						<div
							className="typography-caption"
							style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
						>
							{`${
								testReports.filter((report) => {
									return report.turnaroundTime == tat;
								}).length
							} ${translation("Reports")}`}
						</div>
					</div>
				</div>
			),
		};
	});

	const folderContainer: React.ReactNode = (
		<div>
			<div className="typography-body-01" style={{ marginBottom: "10px" }}>
				{translation("TestReportsByTAT")}
			</div>
			<CustomRadioGroup
				className="client-view-test-report-card-radio-group "
				options={iconCardRadioOptions}
				selectedOption={selectedFolder}
				onChange={(e) => {
					setSeletedFolder(e.target.value);
				}}
			/>
		</div>
	);

	const folderContentContainer: React.ReactNode = (
		<div>
			<div className="test-report-folder-contant-header">
				<div className="placeholder"></div>

				<div>
					<div
						style={{
							display: testReportsInFolder.length == 0 ? "none" : "initial",
						}}
					>
						<BlueButton
							key="test-report-download-folder-button"
							id="test-report-download-folder-button"
							children={translation("DownloadAll")}
							onClick={() => {
								downloadTestReports(
									testReportsInFolder.map((report) => {
										return report.reportId;
									})
								);
							}}
						></BlueButton>
					</div>
				</div>
			</div>
			<div style={{ marginTop: "20px" }}>
				{testReportsInFolder.length == 0 && (
					<div className="typography-heading-02 no-reports-container">
						{translation("NoReportsAvailable")}
					</div>
				)}
				{testReportsInFolder.map((report) => {
					return (
						<div key={report.reportId}>
							<div
								className="order-test-report-item-row"
								onClick={() => {
									downloadTestReports([report.reportId]);
								}}
							>
								<div style={{ maxWidth: "80%" }}>
									<div
										className="typography-caption"
										style={{
											color: report.clientDownloaded
												? NeutralColor.NEUTRAL_GREY_500
												: PrimaryColor.PRIMARY_600,
										}}
									>
										{`${translation("Uploadedon")}: ${formatCustomDate(
											report.inserted as Date
										)}`}
									</div>
									<div
										style={{
											display: "flex",
											gap: "5px",
											alignItems: "center",
										}}
									>
										{!report.clientDownloaded && (
											<span
												style={{
													width: "8px",
													height: "8px",
													minWidth: "8px",
													minHeight: "8px",
													borderRadius: "50%",
													backgroundColor: SemanticColor.ERROR,
												}}
											></span>
										)}
										<div className="typography-body-02 overflow-ellipsis-text">
											{`${report.fileName}.${report.format}`}
										</div>
									</div>
								</div>
								<div>
									<DownloadIcon></DownloadIcon>
								</div>
							</div>
							<Divider orientation="horizontal" flexItem></Divider>
						</div>
					);
				})}
			</div>
		</div>
	);

	const drawerContent: React.ReactNode = (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				gap: "15px",
			}}
		>
			{loading || !selectedOrder ? (
				<ClientOrderReportDrawerSkeletonUI />
			) : (
				<>
					{folderContainer}
					{folderContentContainer}
				</>
			)}
		</div>
	);

	return (
		<CustomDrawer
			anchor={"right"}
			open={open}
			setOpen={setOpen}
			headerText={translation("TestResults")}
			headerDescription={translation("TestResultsDescription")}
		>
			{drawerContent}
		</CustomDrawer>
	);
}
